 
import { Component, ElementRef, inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';

import { EventDelegatorService } from '@services/event-delegator.service';
import { ChartLibraryService } from '@services/chart-library.service';
import { MenuService } from '@services/menu.service';
import { escape } from 'lodash';

declare const L;

@Component({
  selector: 'app-heatmap',
  standalone: true,
  imports: [],
  templateUrl: './heatmap.component.html',
  styleUrls: ['./heatmap.component.scss'],
})
export class HeatmapComponent implements OnInit, OnDestroy {
  @ViewChild('map', { static: true }) mapElement: ElementRef;
  @Input() data;
  features: any[] = [];
  map: any;
  eventBus: any[] = [];

  manuService = inject(MenuService);

  /**
   * heat map data is in the format od { 'mapData': { features: [] },'type': 'heat-map'}
   */

  constructor(
    private eventDelegatorService: EventDelegatorService,
    private chartLibraryService: ChartLibraryService
  ) { }

  ngOnInit(): void {
    this.init();
    this.showOrHidePngDownloadButton(true);
    this.eventBus.push(
      this.eventDelegatorService.on('leaflet-export-map', (data) => {
        this.chartLibraryService.exportLeafletImage(this.map, data.filename);
      })
    );

    this.eventBus.push(
      this.eventDelegatorService.on('ngx-event-analyze', (ev) => {
        if (ev.action === 'print-map') {
          const name = `${ev.data.name}_heatmap`;
          this.chartLibraryService.exportLeafletImage(this.map, name);
        }
      })
    );
  }

  showOrHidePngDownloadButton(visible: boolean) {
    this.manuService.updateMenuItem('export_png', { visible });
  }

  ngOnDestroy(): void {
    this.eventBus.forEach((e) => {
      e?.unsubscribe();
    });
    this.showOrHidePngDownloadButton(false);
  }

  init() {
    if (this.map) {
      this.map.remove();
    }
    this.initMap(this.data);
  }

  initMap(data) {
    let features: any;
    // var map: any;
    let analyticsData;
    const addressPoints = [];
    if (data.mapData) {
      features = data.mapData.features || [];
      analyticsData = data.mapData.analytics;
    }

    this.map = L.map(this.mapElement.nativeElement, {
      maxBoundsViscosity: 1.0,
      zoomControl: true,
      scrollWheelZoom: true,
      touchZoom: true,
      preferCanvas: true,
      // doubleClickZoom: this.mapControls,
      boxZoom: true,
      attributionControl: false,
      keyboard: true,
    })
      .setView([15.395158, 19.062826], 2)
      .whenReady(() => {
        setTimeout(() => {
          this.map.invalidateSize();
        }, 100);
      });
    const options = {
      id: 'mapbox/light-v9',
      tileSize: 512,
      maxZoom: 18,
      zoomOffset: -1,
      minZoom: 1,
      continuousWorld: false,
      noWrap: true,
      bounds: [[-90, -180], [90, 180]]
    };
    const tiles = this.chartLibraryService.getMapTiles(L, options);
    tiles.addTo(this.map);

    // add heat layers on map
    features?.forEach((e) => {
      const pt = e.geometry.coordinates;
      if (pt.length === 2) {
        pt.push(700);
      }
      addressPoints.push(pt);
    });
    const heat = L.heatLayer(addressPoints, {
      radius: 15,
    }).addTo(this.map);

    // add legends on map
    const legend = L.control({ position: 'bottomleft' });
    legend.onAdd = function () {
      const div = L.DomUtil.create('div', 'infolegend legend');
      div.innerHTML += '<i style="background:red"></i> ' + ('High' + '<br>');
      div.innerHTML += '<i style="background:orange"></i> ' + '<br>';
      div.innerHTML += '<i style="background:yellow"></i> ' + '<br>';
      div.innerHTML += '<i style="background:green"></i> ' + '<br>';
      div.innerHTML += '<i style="background:blue"></i> ' + '<br>';
      div.innerHTML += '<i style="background:indigo"></i> ' + '<br>';
      div.innerHTML += '<i style="background:violet"></i> ' + ('Low' + '<br>');
      return div;
    };
    legend.addTo(this.map);

    // add analytics data
    const analytics_legend = L.control({
      position: 'topleft',
    });
    if (analyticsData) {
      analytics_legend.onAdd = function () {
        this._div = L.DomUtil.create('div', 'analytics_legend'); // create a div with a class "info"
        this.update();
        return this._div;
      };
      let key; let value;
      for (const i in analyticsData) {
        key = i;
        value = analyticsData[i].toFixed(2);
      }
      // method that we will use to update the control based on feature properties passed
      analytics_legend.update = function () {
        this._div.innerHTML = `<b>Analytics</b><br/><h4>${escape(key)}=${value}</h4>`;
      };
      analytics_legend.addTo(this.map);
    }
  }

  redrowMap() {
    this.init();
  }
}
